import {
    GALLERY_MEDIA_SOURCE,
    GALLERY_MEDIA_SOURCE_OPTIONS,
    GALLERY_MEDIA_TYPE,
    getGalleryMediaType,
    getGalleryVideoEmbedData,
} from '@containers/Art/Galleries/Galleries.constants';
import { htmlToPlainSearchText } from '@containers/Art/News/News.constants';

export const ARTICLE_BLOCK_TYPE = {
    FILE: 'file',
    MEDIA: 'media',
    TEXT: 'text',
    TITLE: 'title',
};

export const ARTICLE_BLOCK_TYPE_OPTIONS = [
    { value: ARTICLE_BLOCK_TYPE.TITLE, label: 'Title' },
    { value: ARTICLE_BLOCK_TYPE.TEXT, label: 'Text' },
    { value: ARTICLE_BLOCK_TYPE.MEDIA, label: 'Media' },
    { value: ARTICLE_BLOCK_TYPE.FILE, label: 'Linked file' },
];

/** Block types whose content is a `Media` object rather than `Title`/`Text`. */
export const blockTypeHasMedia = (blockType) => {
    return blockType === ARTICLE_BLOCK_TYPE.MEDIA || blockType === ARTICLE_BLOCK_TYPE.FILE;
};

/** Default label for a linked-file block's download link, used until the user edits it. */
export const ARTICLE_FILE_LINK_TEXT_DEFAULT = 'download file';

/** Row layout template persisted on each article row (`Row.Layout`). */
export const ARTICLE_ROW_LAYOUT = {
    ONE_COL: '1col',
    TWO_33_66: '2-33-66',
    TWO_50_50: '2-50-50',
    TWO_66_33: '2-66-33',
    THREE_COL: '3col',
};

const ARTICLE_ROW_LAYOUT_VALUES = Object.values(ARTICLE_ROW_LAYOUT);

/** UI + picker: value is persisted on `Row.Layout`. */
export const ARTICLE_ROW_LAYOUT_OPTIONS = [
    {
        value: ARTICLE_ROW_LAYOUT.ONE_COL,
        label: '1 column',
        shortLabel: '1',
    },
    {
        value: ARTICLE_ROW_LAYOUT.TWO_33_66,
        label: '2 columns (⅓ · ⅔)',
        shortLabel: '⅓ ⅔',
    },
    {
        value: ARTICLE_ROW_LAYOUT.TWO_50_50,
        label: '2 columns (½ · ½)',
        shortLabel: '½ ½',
    },
    {
        value: ARTICLE_ROW_LAYOUT.TWO_66_33,
        label: '2 columns (⅔ · ⅓)',
        shortLabel: '⅔ ⅓',
    },
    {
        value: ARTICLE_ROW_LAYOUT.THREE_COL,
        label: '3 columns',
        shortLabel: '3',
    },
];

/** @deprecated Legacy segmented control used column counts; prefer ARTICLE_ROW_LAYOUT. */
export const ARTICLE_ROW_COLUMN_OPTIONS = [
    { value: 1, label: '1 column' },
    { value: 2, label: '2 columns' },
    { value: 3, label: '3 columns' },
];

export const ARTICLE_MEDIA_SOURCE = GALLERY_MEDIA_SOURCE;
export const ARTICLE_MEDIA_SOURCE_OPTIONS = GALLERY_MEDIA_SOURCE_OPTIONS;
/** Extends the shared image/video gallery type with a 'file' type for linked-file blocks. */
export const ARTICLE_MEDIA_TYPE = { ...GALLERY_MEDIA_TYPE, FILE: 'file' };

export const normalizeArticleBlockType = (blockType) => {
    return Object.values(ARTICLE_BLOCK_TYPE).includes(blockType) ? blockType : ARTICLE_BLOCK_TYPE.TEXT;
};

export const normalizeArticleColumnCount = (columnCount) => {
    const normalizedColumnCount = Number(columnCount);
    return [1, 2, 3].includes(normalizedColumnCount) ? normalizedColumnCount : 1;
};

export const normalizeArticleRowLayoutKey = (layout, fallbackLayout = ARTICLE_ROW_LAYOUT.ONE_COL) => {
    return ARTICLE_ROW_LAYOUT_VALUES.includes(layout) ? layout : fallbackLayout;
};

export const inferArticleRowLayoutFromColumnCount = (columnCount) => {
    const n = normalizeArticleColumnCount(columnCount);
    if (n === 1) return ARTICLE_ROW_LAYOUT.ONE_COL;
    if (n === 2) return ARTICLE_ROW_LAYOUT.TWO_50_50;
    return ARTICLE_ROW_LAYOUT.THREE_COL;
};

export const getArticleRowLayoutColumnCount = (layout) => {
    const key = normalizeArticleRowLayoutKey(layout);
    if (key === ARTICLE_ROW_LAYOUT.ONE_COL) return 1;
    if (
        key === ARTICLE_ROW_LAYOUT.TWO_33_66 ||
        key === ARTICLE_ROW_LAYOUT.TWO_50_50 ||
        key === ARTICLE_ROW_LAYOUT.TWO_66_33
    ) {
        return 2;
    }
    return 3;
};

/** CSS `grid-template-columns` for editor + preview (desktop); mobile uses single column via media query. */
export const getArticleRowLayoutCssGridTemplate = (layout) => {
    const key = normalizeArticleRowLayoutKey(layout);
    switch (key) {
        case ARTICLE_ROW_LAYOUT.ONE_COL:
            return 'minmax(0, 1fr)';
        case ARTICLE_ROW_LAYOUT.TWO_33_66:
            return 'minmax(0, 1fr) minmax(0, 2fr)';
        case ARTICLE_ROW_LAYOUT.TWO_50_50:
            return 'minmax(0, 1fr) minmax(0, 1fr)';
        case ARTICLE_ROW_LAYOUT.TWO_66_33:
            return 'minmax(0, 2fr) minmax(0, 1fr)';
        case ARTICLE_ROW_LAYOUT.THREE_COL:
            return 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)';
        default:
            return 'minmax(0, 1fr)';
    }
};

export const getArticleMediaType = getGalleryMediaType;
export const getArticleVideoEmbedData = getGalleryVideoEmbedData;

export const ARTICLE_TITLE_MAX_LENGTH = 2000;
export const ARTICLE_AUTHOR_NAME_MAX_LENGTH = 250;
export const ARTICLE_SLUG_MAX_LENGTH = 200;

export const DEFAULT_ARTICLES_FILTERS = {
    text: null,
    userCreated: null,
};

/** Builds a URL slug from an article title. */
export const buildArticleSlugFromTitle = (title) => {
    return String(title || '')
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, ARTICLE_SLUG_MAX_LENGTH);
};

/** Keeps only characters valid in a manually edited slug. */
export const sanitizeArticleSlugInput = (value) => {
    return String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, '')
        .slice(0, ARTICLE_SLUG_MAX_LENGTH);
};

/** True when the slug is still the auto-generated value for the given title. */
export const isArticleSlugSyncedWithTitle = (title, slug) => {
    return sanitizeArticleSlugInput(slug) === buildArticleSlugFromTitle(title);
};

const sortArticleElementsByPosition = (items = []) => {
    return [...items].sort((first, second) => first.Position - second.Position);
};

const normalizeArticleHeadingPlain = (value) => {
    return String(value || '')
        .trim()
        .replace(/\s+/g, ' ');
};

/**
 * Plain text for search: article title plus block Title and Text fields in layout order (HTML stripped).
 */
export const buildArticleSearchText = (article = {}) => {
    const parts = [];

    const articleTitle = normalizeArticleHeadingPlain(article.Title);
    if (articleTitle) {
        parts.push(articleTitle);
    }

    const rows = sortArticleElementsByPosition(article.Rows || []);
    for (const row of rows) {
        const columns = sortArticleElementsByPosition(row.Columns || []);
        for (const column of columns) {
            const blocks = sortArticleElementsByPosition(column.Blocks || []);
            for (const block of blocks) {
                const blockTitlePlain = htmlToPlainSearchText(block.Title);
                const blockTextPlain = htmlToPlainSearchText(block.Text);
                if (blockTitlePlain) {
                    parts.push(blockTitlePlain);
                }
                if (blockTextPlain) {
                    parts.push(blockTextPlain);
                }
            }
        }
    }

    return parts.join(' ').replace(/\s+/g, ' ').trim();
};
